/**
 * Copyright © 2017-2023 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding the
 * use or licensing of K4D's copyrighted materials, please contact us at contact@k4d.com.br.
 * "Knowledge for Decisions (K4D)" and the slogan "Using data to make precise decisions" are
 * registered trademarks of Knowledge for Decisions (K4D).
 */

// import axios, { AxiosInstance } from "axios"; // Removed unused imports
import { clientMocks } from "@authien/mocks";
import {
  InvalidCredentialException,
  InvalidUserException,
} from "../../../shared/utils";
import { OAuth2Type } from "@shared/contexts";

// interface OAuth2Config { // Removed unused interface
//   BASE_URL?: string;
//   CLIENT_ID?: string;
//   CLIENT_SECRET?: string;
//   TIMEOUT?: string;
//   TOKEN_ENDPOINT_URL?: string;
//   CLIENT_ENDPOINT_URL?: string;
// }

export interface User {
  username: string;
  telephone: string;
  password: string;
  name: string;
}

export interface Client {
  client_id: string;
  clientSecret: string;
  redirect_uris: Array<string>;
  clientLogoURL: string;
  clientName: string;
  clientToken?: string;
  response_types: Array<string>;
  scopes: Array<string>;
  // Client-specific branding
  clientBranding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoBase64?: string;
    customTitle?: string;
    customSubtitle?: string;
  };
}

class OAuth2Service {

  /**
   * @param username
   * @throws InvalidCredentialException
   * @returns User
   */
  checkIdentityFormat(username: string): string {
    const emailPattern = /^[a-zA-Z0-9._+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    if (emailPattern.test(username)) return username;
      
    throw new InvalidUserException(username);
  }

  checkPasswordFormat(password: string): string {
    
    if (password.length > 7) return password;
      
    throw new InvalidCredentialException();
  }

  private matchesClient(
    client: Client,
    client_id: string,
    redirect_uri: string,
    response_type: string,
    scope: string
  ): boolean {
    return client_id === client.client_id &&
      client.response_types.includes(response_type) &&
      client.redirect_uris.includes(redirect_uri) &&
      client.scopes.includes(scope);
  }

  isClientValid(
    client_id: string,
    redirect_uri: string,
    response_type: string,
    scope: string
  ): boolean {
    return clientMocks.some(client => this.matchesClient(client, client_id, redirect_uri, response_type, scope));
  }

  getClientInformation(oauthParams: OAuth2Type): Client | null {
    const { client_id, redirect_uri, response_type, scope } = oauthParams;
    return clientMocks.find(client => this.matchesClient(client, client_id, redirect_uri, response_type, scope)) ?? null;
  }
}

export default OAuth2Service;
